import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { locationConfig } from '@shared/location';

interface PlaceMiniMapProps {
  destinationLat: number;
  destinationLon: number;
  destinationLabel: string;
}

/**
 * Compact 200px map showing the user's home base and a destination pin,
 * connected by a dashed line. Auto-fits both pins in view.
 *
 * Reads home-base coordinates and bounds from `locationConfig.geo` so
 * this component is fully portable to other locations — nothing about
 * Babcock Ranch is hardcoded here.
 *
 * Tiles: OpenStreetMap default — no API key, no domain allowlist. If
 * we want a softer basemap later, Stadia Alidade Smooth (with a
 * registered key) or CARTO Voyager are drop-in URL replacements.
 */
export function PlaceMiniMap({ destinationLat, destinationLon, destinationLabel }: PlaceMiniMapProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const { homeBase, zoomDefault } = locationConfig.geo;

    const map = L.map(containerRef.current, {
      zoomControl: false,
      attributionControl: true,
      scrollWheelZoom: false, // avoid hijacking page scroll on mobile
      dragging: true,
      doubleClickZoom: true,
      touchZoom: true,
    }).setView([homeBase.lat, homeBase.lon], zoomDefault);

    // OpenStreetMap default tiles: no API key, no domain allowlist, free.
    // If we ever want a softer basemap that matches the palette better,
    // Stadia Alidade Smooth (register a free key) or CARTO Voyager are
    // drop-in replacements — change the URL template and attribution.
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors',
    }).addTo(map);

    // Coral home pin (rental / current base).
    const homeIcon = L.divIcon({
      className: 'mini-map-pin',
      html: `<div class="mini-map-pin__inner mini-map-pin__inner--home" title="${homeBase.label}"></div>`,
      iconSize: [18, 18],
      iconAnchor: [9, 9],
    });
    const homeMarker = L.marker([homeBase.lat, homeBase.lon], { icon: homeIcon }).addTo(map);
    homeMarker.bindTooltip(homeBase.label, { direction: 'top', offset: [0, -8] });

    // Teal destination pin.
    const destIcon = L.divIcon({
      className: 'mini-map-pin',
      html: `<div class="mini-map-pin__inner mini-map-pin__inner--dest" title="${destinationLabel}"></div>`,
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });
    const destMarker = L.marker([destinationLat, destinationLon], { icon: destIcon }).addTo(map);
    destMarker.bindTooltip(destinationLabel, { direction: 'top', offset: [0, -8] });

    // Dashed connector line.
    L.polyline(
      [
        [homeBase.lat, homeBase.lon],
        [destinationLat, destinationLon],
      ],
      {
        // Solar Reserve accent; was the retired coastal teal #0E7C86.
        color: '#173D4E',
        weight: 2,
        opacity: 0.7,
        dashArray: '6, 6',
      },
    ).addTo(map);

    // Fit bounds to include both pins, with padding.
    const bounds = L.latLngBounds([
      [homeBase.lat, homeBase.lon],
      [destinationLat, destinationLon],
    ]);
    map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [destinationLat, destinationLon, destinationLabel]);

  return (
    <div className="place-mini-map-wrapper card" style={{ padding: 0, overflow: 'hidden' }}>
      <div
        ref={containerRef}
        className="place-mini-map"
        role="img"
        aria-label={`Map from ${locationConfig.geo.homeBase.label} to ${destinationLabel}`}
        style={{ height: 200, width: '100%' }}
      />
    </div>
  );
}
